import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * AURUM — Illuminated Manuscript / Gold Leaf
 *
 * Deep emerald + gold leaf + parchment cream. Hand-illuminated medieval
 * manuscript meets luxury wedding invite. Drop caps in ornate frames,
 * gilded borders that shimmer, 3D page-turn signature moment.
 *
 *  ACT I   COVER — Ornate gilded frame with shimmering gold gradient,
 *          giant illuminated capital, Latin tagline, decorative knots
 *          in 4 corners, parchment texture.
 *  ACT II  PINNED 3D PAGE TURN — A book sits center stage. As you
 *          scroll, pages turn one by one with rotateY(180°), revealing
 *          new spreads behind. 5 pages × 180° = full book reading.
 *  ACT III ILLUMINATED INITIAL — A massive drop cap initial letter
 *          with intricate decorative frame around it; story text wraps
 *          to its right with vertical rule.
 *  ACT IV  GILDED TIMELINE — Vertical gold-leaf timeline with 5 dates,
 *          each anchor draws in with gold shimmer.
 *  ACT V   VENUE PORTRAIT — Framed photo with corner illumination,
 *          ornate scroll-banner caption.
 *  ACT VI  RSVP card with stamped wax seal + gilded border + tilt.
 */
export function AurumLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const bookRef = useRef<HTMLDivElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthName = d?.toLocaleString("tr-TR", { month: "long" }) ?? "—";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const year = d?.getFullYear() ?? 2026;

  const [firstName, lastName] = title.split("&").map((s) => s.trim());
  const dropCap = (firstName?.[0] ?? "M").toUpperCase();
  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.8, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: COVER entrance ──────────────────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".au-frame", { scale: 0.85, opacity: 0, duration: 1.6 });
      tl.from(".au-corner-knot", {
        scale: 0,
        rotate: (i) => [(-90), (90), (-90), (90)][i],
        stagger: 0.1,
        duration: 1,
        ease: "back.out(1.5)",
      }, 0.5);

      const titleSplit = new SplitText(".au-cover-title", { type: "chars" });
      tl.from(titleSplit.chars, { y: 40, opacity: 0, stagger: 0.04, duration: 1.2 }, 0.7);

      tl.from(".au-cover-rule", { scaleX: 0, transformOrigin: "center", duration: 1.4, ease: "expo.out" }, 0.9);
      tl.from(".au-cover-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 1 }, 1.1);
      tl.from(".au-cover-wax", { scale: 0, rotate: -45, duration: 0.8, ease: "back.out(1.8)" }, 1.5);

      // ── ACT II: PINNED 3D PAGE TURN ────────────────────────────────
      const book = bookRef.current;
      if (book) {
        const pages = document.querySelectorAll<HTMLElement>(".au-book-page");
        const bookTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".au-book-section",
            start: "top top",
            end: `+=${pages.length * 100}%`,
            pin: true,
            scrub: 1,
          },
        });

        pages.forEach((page, i) => {
          bookTl.to(page, {
            rotateY: -180,
            duration: 1,
            ease: "power2.inOut",
          }, i);
          // Add shadow during flip
          bookTl.to(page, {
            boxShadow: "0 0 60px rgba(0,0,0,0.5)",
            duration: 0.5,
          }, i);
          bookTl.to(page, {
            boxShadow: "0 0 0 rgba(0,0,0,0)",
            duration: 0.5,
          }, i + 0.5);
        });

        // Page counter
        const pageCounter = document.querySelector<HTMLElement>(".au-page-counter");
        if (pageCounter) {
          ScrollTrigger.create({
            trigger: ".au-book-section",
            start: "top top",
            end: `+=${pages.length * 100}%`,
            onUpdate(self) {
              const page = Math.min(pages.length, Math.max(1, Math.ceil(self.progress * pages.length) + 1));
              pageCounter.textContent = `${["I","II","III","IV","V","VI"][page - 1] ?? "VI"} / VI`;
            },
          });
        }
      }

      // ── ACT III: ILLUMINATED INITIAL ──────────────────────────────
      gsap.from(".au-dropcap", {
        rotateY: -90,
        opacity: 0,
        scale: 0.8,
        transformOrigin: "left center",
        duration: 1.6,
        ease: "expo.out",
        scrollTrigger: { trigger: ".au-initial-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".au-dropcap-frame > *", {
        scale: 0,
        opacity: 0,
        stagger: 0.06,
        duration: 0.8,
        ease: "back.out(1.5)",
        scrollTrigger: { trigger: ".au-initial-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".au-story-line", {
        opacity: 0,
        x: 30,
        stagger: 0.1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".au-initial-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      // ── ACT IV: GILDED TIMELINE ───────────────────────────────────
      gsap.from(".au-time-rail", {
        scaleY: 0,
        transformOrigin: "top",
        duration: 2,
        ease: "expo.out",
        scrollTrigger: { trigger: ".au-timeline-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      document.querySelectorAll(".au-time-entry").forEach((entry, i) => {
        gsap.from(entry, {
          x: i % 2 === 0 ? -60 : 60,
          opacity: 0,
          duration: 1.2,
          delay: i * 0.15,
          ease: "expo.out",
          scrollTrigger: { trigger: ".au-timeline-section", start: "top 65%", toggleActions: "play reverse play reverse" },
        });
      });
      gsap.from(".au-time-dot", {
        scale: 0,
        stagger: 0.12,
        duration: 0.7,
        ease: "back.out(1.8)",
        scrollTrigger: { trigger: ".au-timeline-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      // ── ACT V: VENUE PORTRAIT ─────────────────────────────────────
      gsap.from(".au-venue-frame", {
        clipPath: "inset(0 50% 0 50%)",
        duration: 1.6,
        ease: "expo.inOut",
        scrollTrigger: { trigger: ".au-venue-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".au-venue-banner", {
        y: -40,
        opacity: 0,
        duration: 1.2,
        ease: "expo.out",
        scrollTrigger: { trigger: ".au-venue-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".au-venue-text", {
        y: 20,
        opacity: 0,
        stagger: 0.12,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".au-venue-section", start: "top 60%", toggleActions: "play reverse play reverse" },
      });

      // Form
      gsap.from(".au-form-card", { opacity: 0, y: 60, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".au-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Decorative corner knot SVG
  const CornerKnot = ({ rotate = 0, className = "" }: { rotate?: number; className?: string }) => (
    <svg className={className} width="80" height="80" viewBox="0 0 80 80" style={{ transform: `rotate(${rotate}deg)` }}>
      <g stroke="#c9a04e" strokeWidth="1.2" fill="none">
        {/* Celtic knot motif */}
        <path d="M 10 40 Q 20 10 40 20 Q 60 30 70 10" />
        <path d="M 10 50 Q 20 70 40 60 Q 60 50 70 70" />
        <circle cx="40" cy="40" r="6" fill="#c9a04e" stroke="none" />
        <circle cx="40" cy="40" r="3" fill="#0d3d2e" />
        <path d="M 20 20 L 60 60" opacity="0.5" />
        <path d="M 60 20 L 20 60" opacity="0.5" />
      </g>
    </svg>
  );

  // Gilded border component
  const GildedBorder = () => (
    <>
      <div className="absolute inset-2 pointer-events-none" style={{ border: "2px solid", borderImage: "linear-gradient(135deg, #c9a04e 0%, #f4d977 30%, #c9a04e 50%, #f4d977 70%, #c9a04e 100%) 1", boxShadow: "inset 0 0 30px rgba(201, 160, 78, 0.15)" }} />
      <div className="absolute inset-4 pointer-events-none" style={{ border: "1px solid rgba(201, 160, 78, 0.3)" }} />
    </>
  );

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#0d3d2e", color: "#f4ecd6", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');
        @keyframes auGoldShimmer {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        .au-gilded-text {
          background: linear-gradient(110deg, #c9a04e 0%, #f4d977 30%, #fef2c0 50%, #f4d977 70%, #c9a04e 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: auGoldShimmer 6s ease-in-out infinite;
          text-shadow: 0 0 40px rgba(244, 217, 119, 0.15);
        }
        .au-gilded-line {
          background: linear-gradient(90deg, transparent, #c9a04e, #f4d977, #c9a04e, transparent);
          background-size: 200% 100%;
          animation: auGoldShimmer 5s ease-in-out infinite;
        }
        @keyframes auMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>

      {/* ═══ PARCHMENT TEXTURE OVERLAY ════════════════════════════════ */}
      <svg className="fixed inset-0 w-full h-full pointer-events-none z-[1] opacity-[0.06] mix-blend-overlay">
        <filter id="au-parchment">
          <feTurbulence type="fractalNoise" baseFrequency="1.3" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0.4 0 0 0 0  0.3 0 0 0 0  0.1 0 0 0 0  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#au-parchment)" />
      </svg>

      {/* Deep emerald gradient base */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at top, #1a5a45 0%, #0d3d2e 50%, #051f17 100%)",
      }} />

      {/* ═══ HEADER ═══════════════════════════════════════════════════ */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]"
        style={{ background: "rgba(13, 61, 46, 0.7)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(201, 160, 78, 0.3)" }}>
        <span className="au-gilded-text font-bold">Aurum</span>
        <span className="italic normal-case text-base tracking-normal" style={{ color: "#f4d977", fontFamily: "'Cormorant Garamond', serif" }}>~ Anno MMXXVI ~</span>
        <span style={{ color: "#c9a04e" }} className="au-page-counter">I / VI</span>
      </header>

      {/* ═══ ACT I: ORNATE COVER ══════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-12">
        <div className="au-frame relative w-full max-w-3xl text-center py-20 px-12 sm:px-20" style={{
          background: "linear-gradient(180deg, #1a5a45 0%, #0d3d2e 100%)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.5), 0 16px 32px rgba(0,0,0,0.3)",
        }}>
          <GildedBorder />

          {/* 4 corner knots */}
          <div className="au-corner-knot absolute -top-6 -left-6"><CornerKnot /></div>
          <div className="au-corner-knot absolute -top-6 -right-6"><CornerKnot rotate={90} /></div>
          <div className="au-corner-knot absolute -bottom-6 -left-6"><CornerKnot rotate={-90} /></div>
          <div className="au-corner-knot absolute -bottom-6 -right-6"><CornerKnot rotate={180} /></div>

          {/* Wax seal medallion top */}
          <div className="au-cover-wax absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full flex items-center justify-center" style={{
            background: "radial-gradient(circle at 30% 30%, #d4423a, #7a1f1f)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.5), inset 0 -3px 5px rgba(0,0,0,0.3)",
            border: "2px solid #c9a04e",
          }}>
            <span className="italic text-xl text-[#f4ecd6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>M&Z</span>
          </div>

          <p className="au-cover-meta text-[10px] uppercase tracking-[0.6em] mb-2" style={{ color: "#c9a04e" }}>— Codex Nuptialis —</p>
          <p className="au-cover-meta italic text-lg mb-12 au-gilded-text">~ Anno Domini MMXXVI ~</p>

          <h1 className="au-cover-title au-gilded-text font-light italic leading-[0.9] tracking-tight" style={{ fontSize: "clamp(3.5rem, 10vw, 8rem)" }}>
            {title}
          </h1>

          <div className="au-cover-rule mx-auto mt-10 mb-3 h-px max-w-sm au-gilded-line" />
          <p className="au-cover-meta italic text-2xl" style={{ color: "#f4d977", fontFamily: "'Cormorant Garamond', serif" }}>
            ~ in matrimonium ~
          </p>
          <div className="au-cover-rule mx-auto mt-3 mb-10 h-px max-w-sm au-gilded-line" />

          <p className="au-cover-meta italic text-xl" style={{ color: "#f4ecd6", fontFamily: "'Cormorant Garamond', serif" }}>
            {day}.{monthShort.toLowerCase()}.{year}
          </p>
          <p className="au-cover-meta mt-3 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#c9a04e" }}>
            ~ {(location ?? "").split(",")[0]} ~
          </p>
        </div>
      </section>

      {/* ═══ ACT II: PINNED 3D PAGE TURN ══════════════════════════════ */}
      <section className="au-book-section relative h-screen overflow-hidden bg-[#0a2e22]">
        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em] z-30" style={{ color: "#c9a04e" }}>II · Liber Cordis</div>
        <div className="absolute top-12 right-12 text-[10px] uppercase tracking-[0.5em] z-30" style={{ color: "#f4d977" }}>~ Scroll to turn ~</div>

        {/* Book stage */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: 1800 }}>
          {/* Spine shadow */}
          <div className="absolute w-1 h-[70vh] bg-black/50 z-10" />

          <div ref={bookRef} className="relative" style={{ width: "min(60vw, 30rem)", height: "min(80vw, 40rem)", transformStyle: "preserve-3d" }}>
            {/* Static right side (back of book - always visible) */}
            <div className="absolute inset-0 flex items-center justify-center p-8 text-center" style={{
              background: "linear-gradient(135deg, #f4ecd6 0%, #d4be8a 100%)",
              color: "#0d3d2e",
              boxShadow: "inset 0 0 60px rgba(0,0,0,0.2)",
            }}>
              <div>
                <p className="text-[10px] uppercase tracking-[0.5em] mb-6" style={{ color: "#7a5e1f" }}>— Finis —</p>
                <p className="font-light italic leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
                  Sıra sizde.
                </p>
                <div className="mx-auto mt-6 w-12 h-px au-gilded-line" />
                <p className="text-xs uppercase tracking-[0.4em] mt-6" style={{ color: "#7a5e1f" }}>{day} · {monthShort} · {year}</p>
              </div>
            </div>

            {/* Pages (each flips on scroll) */}
            {[
              { front: { label: "I", title: "Anno I", body: "İlk bahar akşamı — beş saat farkındalıksız geçti." }, back: { label: "II", title: "Anno II", body: "Roma, Paris, İstanbul — üç şehir, sonsuz hatıra." } },
              { front: { label: "III", title: "Anno III", body: "Plajda yıldızlar yandı. Bir tek söz." }, back: { label: "IV", title: "Anno IV", body: "Bugün bu hikâyenin altıncı bölümünü açıyoruz." } },
              { front: { label: "V", title: "Locus", body: location ?? "—" }, back: { label: "VI", title: "Tempus", body: `${day} ${monthName} ${year}` } },
            ].map((p, i) => (
              <div
                key={i}
                className="au-book-page absolute inset-0"
                style={{
                  transformStyle: "preserve-3d",
                  transformOrigin: "left center",
                  zIndex: 10 - i,
                }}
              >
                {/* Front of page */}
                <div className="absolute inset-0 flex items-center justify-center p-8 text-center" style={{
                  background: "linear-gradient(135deg, #f4ecd6 0%, #d4be8a 100%)",
                  color: "#0d3d2e",
                  backfaceVisibility: "hidden",
                  boxShadow: "inset 0 0 60px rgba(0,0,0,0.2)",
                  border: "1px solid rgba(122, 94, 31, 0.3)",
                }}>
                  <div className="relative w-full">
                    <div className="absolute inset-2 pointer-events-none" style={{ border: "1px solid rgba(122, 94, 31, 0.25)" }} />
                    <p className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "#7a5e1f" }}>— Page {p.front.label} —</p>
                    <h3 className="au-gilded-text font-light italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
                      {p.front.title}
                    </h3>
                    <p className="italic text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      "{p.front.body}"
                    </p>
                    <div className="mx-auto mt-6 w-10 h-px au-gilded-line" />
                  </div>
                </div>
                {/* Back of page */}
                <div className="absolute inset-0 flex items-center justify-center p-8 text-center" style={{
                  background: "linear-gradient(225deg, #f4ecd6 0%, #d4be8a 100%)",
                  color: "#0d3d2e",
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  boxShadow: "inset 0 0 60px rgba(0,0,0,0.2)",
                  border: "1px solid rgba(122, 94, 31, 0.3)",
                }}>
                  <div className="relative w-full">
                    <div className="absolute inset-2 pointer-events-none" style={{ border: "1px solid rgba(122, 94, 31, 0.25)" }} />
                    <p className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "#7a5e1f" }}>— Page {p.back.label} —</p>
                    <h3 className="au-gilded-text font-light italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
                      {p.back.title}
                    </h3>
                    <p className="italic text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      "{p.back.body}"
                    </p>
                    <div className="mx-auto mt-6 w-10 h-px au-gilded-line" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT III: ILLUMINATED INITIAL ═════════════════════════════ */}
      <section className="au-initial-section relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>III · Capitulum I</p>
          <h2 className="text-center font-light italic leading-tight mb-20 au-gilded-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            Historia Nostra
          </h2>

          <div className="grid sm:grid-cols-12 gap-8 items-start">
            {/* Illuminated drop cap */}
            <div className="sm:col-span-4 flex justify-center">
              <div className="au-dropcap-frame relative" style={{ width: 260, height: 260 }}>
                {/* Ornate frame */}
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(135deg, #1a5a45 0%, #0d3d2e 100%)",
                  border: "3px double #c9a04e",
                  boxShadow: "inset 0 0 30px rgba(201, 160, 78, 0.2), 0 16px 32px rgba(0,0,0,0.4)",
                }} />
                <div className="absolute inset-3 pointer-events-none" style={{ border: "1px solid rgba(201, 160, 78, 0.4)" }} />
                {/* Corner ornaments inside frame */}
                <svg className="absolute top-1 left-1" width="30" height="30" viewBox="0 0 30 30"><path d="M 0 15 Q 5 5 15 0" stroke="#c9a04e" strokeWidth="1" fill="none" /><circle cx="15" cy="15" r="2" fill="#c9a04e" /></svg>
                <svg className="absolute top-1 right-1" width="30" height="30" viewBox="0 0 30 30" style={{ transform: "scaleX(-1)" }}><path d="M 0 15 Q 5 5 15 0" stroke="#c9a04e" strokeWidth="1" fill="none" /><circle cx="15" cy="15" r="2" fill="#c9a04e" /></svg>
                <svg className="absolute bottom-1 left-1" width="30" height="30" viewBox="0 0 30 30" style={{ transform: "scaleY(-1)" }}><path d="M 0 15 Q 5 5 15 0" stroke="#c9a04e" strokeWidth="1" fill="none" /><circle cx="15" cy="15" r="2" fill="#c9a04e" /></svg>
                <svg className="absolute bottom-1 right-1" width="30" height="30" viewBox="0 0 30 30" style={{ transform: "scale(-1)" }}><path d="M 0 15 Q 5 5 15 0" stroke="#c9a04e" strokeWidth="1" fill="none" /><circle cx="15" cy="15" r="2" fill="#c9a04e" /></svg>

                <p className="au-dropcap absolute inset-0 flex items-center justify-center font-light au-gilded-text" style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 180,
                  lineHeight: 1,
                }}>
                  {dropCap}
                </p>
              </div>
            </div>

            {/* Story text wraps to the right */}
            <div className="sm:col-span-8 sm:pl-4" style={{ borderLeft: "1px solid rgba(201, 160, 78, 0.3)" }}>
              <div className="pl-6">
                <p className="au-story-line text-[10px] uppercase tracking-[0.4em] mb-6" style={{ color: "#c9a04e" }}>
                  {firstName} & {lastName} · Anno Domini {year}
                </p>
                {storyLines.map((line, i) => (
                  <p key={i} className="au-story-line italic mb-4 leading-[1.6]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.15rem, 1.7vw, 1.5rem)", color: "#f4ecd6" }}>
                    {line.trim()}
                  </p>
                ))}
                <div className="au-story-line mt-8 flex items-baseline justify-end gap-2" style={{ color: "#c9a04e" }}>
                  <span className="italic text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>~ Finis Capituli ~</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: GILDED TIMELINE ══════════════════════════════════ */}
      <section className="au-timeline-section relative py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>IV · Chronologia</p>
          <h2 className="text-center font-light italic leading-tight mb-20 au-gilded-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            Five years, one story.
          </h2>

          <div className="relative">
            {/* Vertical rail */}
            <div className="au-time-rail absolute left-1/2 top-0 bottom-0 w-px au-gilded-line" />

            {[
              { year: "2024", title: "Prima Visio", note: "İlk buluşma" },
              { year: "2024", title: "Iter Communis", note: "Birlikte ilk yol" },
              { year: "2025", title: "Confessio", note: "Söz" },
              { year: "2025", title: "Nuptialia", note: "Hazırlık" },
              { year: year.toString(), title: "Hodie", note: "Bugün" },
            ].map((e, i) => (
              <div key={i} className="au-time-entry relative grid grid-cols-2 gap-8 mb-12" style={{ direction: i % 2 === 0 ? "ltr" : "rtl" }}>
                <div className="text-right" style={{ direction: "ltr" }}>
                  <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "#c9a04e" }}>Anno {e.year}</p>
                  <p className="italic au-gilded-text mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 2.5vw, 2rem)" }}>{e.title}</p>
                  <p className="italic text-[#f4ecd6]/70 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{e.note}</p>
                </div>
                {/* Anchor dot on rail */}
                <div className="au-time-dot absolute left-1/2 top-2 -translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center" style={{
                  background: "linear-gradient(135deg, #c9a04e, #f4d977)",
                  boxShadow: "0 0 20px rgba(244, 217, 119, 0.5)",
                }}>
                  <span className="w-2 h-2 rounded-full bg-[#0d3d2e]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT V: VENUE PORTRAIT ═══════════════════════════════════ */}
      <section className="au-venue-section relative py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="au-venue-banner text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>V · Locus Nuptialis</p>
            <h2 className="au-gilded-text font-light italic leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}>
              {location?.split(",")[0] ?? "—"}
            </h2>
          </div>

          <div className="au-venue-frame relative aspect-[16/9] overflow-hidden" style={{
            border: "3px double #c9a04e",
            padding: 10,
            background: "#0a2e22",
          }}>
            <img src={galleryImages[4]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.15) saturate(0.95) brightness(0.85)" }} />
            <div className="absolute inset-12 pointer-events-none" style={{ border: "1px solid rgba(201, 160, 78, 0.3)" }} />
            {/* Corner ornaments on photo */}
            <div className="absolute top-3 left-3"><CornerKnot /></div>
            <div className="absolute top-3 right-3"><CornerKnot rotate={90} /></div>
            <div className="absolute bottom-3 left-3"><CornerKnot rotate={-90} /></div>
            <div className="absolute bottom-3 right-3"><CornerKnot rotate={180} /></div>
          </div>

          <div className="au-venue-text mt-8 text-center">
            <p className="italic text-lg text-[#f4ecd6]/70 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {location?.split(",").slice(1).join(",").trim() ?? ""}
            </p>
            <p className="text-[11px] uppercase tracking-[0.5em]" style={{ color: "#c9a04e" }}>
              {day} {monthName.toLowerCase()} {year} · {d?.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: RSVP card ═══════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="au-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out text-center"
          style={{
            background: "linear-gradient(135deg, #1a5a45 0%, #0d3d2e 100%)",
            boxShadow: "0 40px 100px rgba(0, 0, 0, 0.7)",
            transformStyle: "preserve-3d",
          }}
        >
          <GildedBorder />
          {/* Corner ornaments */}
          <div className="absolute -top-4 -left-4"><CornerKnot /></div>
          <div className="absolute -top-4 -right-4"><CornerKnot rotate={90} /></div>
          <div className="absolute -bottom-4 -left-4"><CornerKnot rotate={-90} /></div>
          <div className="absolute -bottom-4 -right-4"><CornerKnot rotate={180} /></div>

          {/* Wax seal on top */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full flex items-center justify-center" style={{
            background: "radial-gradient(circle at 30% 30%, #d4423a, #7a1f1f)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.5), inset 0 -3px 5px rgba(0,0,0,0.3)",
            border: "2px solid #c9a04e",
          }}>
            <span className="italic text-xl text-[#f4ecd6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>RSVP</span>
          </div>

          <p className="text-[10px] uppercase tracking-[0.6em] mb-3 mt-6" style={{ color: "#c9a04e" }}>VI · Responsum</p>
          <h3 className="au-gilded-text font-light italic mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Confirmatio
          </h3>
          <p className="italic mb-10" style={{ color: "#f4d977", fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}>
            ~ adveniet vestra responsio ~
          </p>
          <div className="mx-auto w-12 h-px mb-10 au-gilded-line" />

          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
